import { Link } from "react-router-dom";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="font-display text-3xl md:text-4xl font-bold">{title}</h1>
        {subtitle && <p className="text-muted-foreground mt-1.5 max-w-2xl">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, text, to, cta }) {
  return (
    <Card className="p-12 text-center border-dashed">
      {Icon && <div className="mx-auto w-14 h-14 rounded-full bg-accent text-primary grid place-items-center mb-4"><Icon className="w-6 h-6" /></div>}
      <h3 className="font-display text-xl font-semibold">{title}</h3>
      {text && <p className="text-sm text-muted-foreground mt-1.5 max-w-sm mx-auto">{text}</p>}
      {to && <Link to={to}><Button className="mt-5 bg-primary text-primary-foreground hover:bg-primary/90">{cta}</Button></Link>}
    </Card>
  );
}

export function ProductCardSkeleton() {
  return (
    <Card className="overflow-hidden">
      <Skeleton className="aspect-square rounded-none" />
      <div className="p-4 space-y-3"><Skeleton className="h-4 w-16" /><Skeleton className="h-5 w-full" /><Skeleton className="h-6 w-24" /><Skeleton className="h-9 w-full" /></div>
    </Card>
  );
}

export function PageLoader({ label = "Loading..." }) {
  return <div role="status" className="text-center py-24 text-muted-foreground">{label}</div>;
}

export const inr = (n) => {
  const v = Number(n || 0);
  return `₹${v.toLocaleString("en-IN", Number.isInteger(v) ? {} : { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const FALLBACK_IMG = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 400'%3E%3Crect width='400' height='400' fill='%23EAE8E3'/%3E%3Cpath d='M200 260V190m0 0c0-34 22-56 60-60 0 38-22 60-60 60zm0 22c0-26-19-45-52-48 0 30 19 48 52 48z' fill='none' stroke='%23A9B3AD' stroke-width='10' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E";

export function Img({ src, alt = "", className }) {
  return <img src={src || FALLBACK_IMG} alt={alt} loading="lazy" className={className}
              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = FALLBACK_IMG; }} />;
}

export function RatingChip({ avg, count, className = "" }) {
  if (!count) return null;
  const good = avg >= 4, mid = avg >= 3;
  return (
    <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-semibold ${good ? "bg-emerald-700 text-white" : mid ? "bg-amber-400 text-amber-950" : "bg-red-700 text-white"} ${className}`}>
      {Number(avg).toFixed(1)} <svg viewBox="0 0 20 20" className="w-2.5 h-2.5 fill-current"><path d="M10 1.5l2.6 5.5 6 .8-4.4 4.2 1.1 6-5.3-2.9-5.3 2.9 1.1-6L1.4 7.8l6-.8z" /></svg>
      <span className="font-normal opacity-90">({count.toLocaleString("en-IN")})</span>
    </span>
  );
}

export function PriceBlock({ price, mrp, discount_pct, unit, size = "md" }) {
  const big = size === "lg";
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
      <span className={`font-display font-bold text-primary ${big ? "text-4xl" : "text-lg sm:text-xl"}`}>{inr(price)}</span>
      {mrp > price && <span className={`line-through text-muted-foreground ${big ? "text-lg" : "text-xs"}`}>{inr(mrp)}</span>}
      {discount_pct > 0 && <span className={`font-semibold text-emerald-700 dark:text-emerald-400 ${big ? "text-lg" : "text-xs"}`}>{discount_pct}% off</span>}
      {unit && <span className="text-xs text-muted-foreground">/ {unit}</span>}
    </div>
  );
}
